import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { QuestionnaireState, QuestionnaireProgress, QuestionSection, QuestionnaireSection } from '@/types'
import { createInitialQuestionnaireState, SECTION_ORDER } from '@/types/questionnaire.types'

const initialState: QuestionnaireState = createInitialQuestionnaireState()

const questionnaireSlice = createSlice({
  name: 'questionnaire',
  initialState,
  reducers: {
    // ========================================
    // MODAL STATE
    // ========================================
    openQuestionnaire: (state) => {
      state.isOpen = true
      if (!state.progress.startedAt) {
        state.progress.startedAt = new Date().toISOString()
      }
    },
    
    closeQuestionnaire: (state) => {
      state.isOpen = false
    },
    
    toggleQuestionnaire: (state) => {
      state.isOpen = !state.isOpen
      if (state.isOpen && !state.progress.startedAt) {
        state.progress.startedAt = new Date().toISOString()
      }
    },
    
    // ========================================
    // NAVIGATION
    // ========================================
    goToSection: (state, action: PayloadAction<QuestionSection>) => {
      state.progress.currentSection = action.payload
      state.progress.currentQuestionIndex = 0
      state.progress.lastUpdated = new Date().toISOString()
    },
    
    goToQuestion: (state, action: PayloadAction<{ section: QuestionSection; index: number }>) => {
      state.progress.currentSection = action.payload.section
      state.progress.currentQuestionIndex = action.payload.index
      state.progress.lastUpdated = new Date().toISOString()
    },
    
    nextQuestion: (state) => {
      const currentSectionData = state.sections.find(s => s.id === state.progress.currentSection)
      if (!currentSectionData) return
      
      if (state.progress.currentQuestionIndex < currentSectionData.questions.length - 1) {
        // More questions in current section
        state.progress.currentQuestionIndex++
      } else {
        // Move to next section
        const currentSectionIndex = SECTION_ORDER.indexOf(state.progress.currentSection)
        if (currentSectionIndex < SECTION_ORDER.length - 1) {
          const nextSection = SECTION_ORDER[currentSectionIndex + 1]
          // Mark current section as completed
          if (!state.progress.completedSections.includes(state.progress.currentSection)) {
            state.progress.completedSections.push(state.progress.currentSection)
          }
          state.progress.currentSection = nextSection
          state.progress.currentQuestionIndex = 0
        } else {
          // Questionnaire complete
          if (!state.progress.completedSections.includes(state.progress.currentSection)) {
            state.progress.completedSections.push(state.progress.currentSection)
          }
          state.isCompleted = true
        }
      }
      state.progress.lastUpdated = new Date().toISOString()
    },
    
    previousQuestion: (state) => {
      if (state.progress.currentQuestionIndex > 0) {
        // More questions in current section
        state.progress.currentQuestionIndex--
      } else {
        // Move to previous section
        const currentSectionIndex = SECTION_ORDER.indexOf(state.progress.currentSection)
        if (currentSectionIndex > 0) {
          const prevSection = SECTION_ORDER[currentSectionIndex - 1]
          const prevSectionData = state.sections.find(s => s.id === prevSection)
          state.progress.currentSection = prevSection
          state.progress.currentQuestionIndex = prevSectionData 
            ? prevSectionData.questions.length - 1 
            : 0
        }
      }
      state.progress.lastUpdated = new Date().toISOString()
    },
    
    // ========================================
    // ANSWERS
    // ========================================
    setAnswer: (state, action: PayloadAction<{ questionId: string; value: unknown }>) => {
      state.progress.answers[action.payload.questionId] = action.payload.value
      state.progress.lastUpdated = new Date().toISOString()
      
      // Remove from skipped if it was skipped before
      state.progress.skippedQuestions = state.progress.skippedQuestions.filter(
        id => id !== action.payload.questionId
      )
    },
    
    clearAnswer: (state, action: PayloadAction<string>) => {
      delete state.progress.answers[action.payload]
      state.progress.lastUpdated = new Date().toISOString()
    },
    
    skipQuestion: (state, action: PayloadAction<string>) => {
      if (!state.progress.skippedQuestions.includes(action.payload)) {
        state.progress.skippedQuestions.push(action.payload)
      }
      state.progress.lastUpdated = new Date().toISOString()
    },
    
    unskipQuestion: (state, action: PayloadAction<string>) => {
      state.progress.skippedQuestions = state.progress.skippedQuestions.filter(
        id => id !== action.payload
      )
      state.progress.lastUpdated = new Date().toISOString()
    },
    
    // ========================================
    // SECTION COMPLETION
    // ========================================
    completeSection: (state, action: PayloadAction<QuestionSection>) => {
      if (!state.progress.completedSections.includes(action.payload)) {
        state.progress.completedSections.push(action.payload)
      }
      state.progress.lastUpdated = new Date().toISOString()
    },
    
    uncompleteSection: (state, action: PayloadAction<QuestionSection>) => {
      state.progress.completedSections = state.progress.completedSections.filter(
        s => s !== action.payload
      )
      state.progress.lastUpdated = new Date().toISOString()
    },
    
    // ========================================
    // QUESTIONNAIRE COMPLETION
    // ========================================
    completeQuestionnaire: (state) => {
      state.isCompleted = true
      state.isOpen = false
      state.progress.completedSections = [...SECTION_ORDER]
      state.progress.lastUpdated = new Date().toISOString()
    },
    
    reopenQuestionnaire: (state) => {
      state.isCompleted = false
      state.isOpen = true
    },
    
    // ========================================
    // SECTIONS (Question Definitions)
    // ========================================
    setSections: (state, action: PayloadAction<QuestionnaireSection[]>) => {
      state.sections = action.payload
    },
    
    // ========================================
    // PROGRESS
    // ========================================
    setProgress: (state, action: PayloadAction<Partial<QuestionnaireProgress>>) => {
      state.progress = { ...state.progress, ...action.payload }
    },
    
    // ========================================
    // FULL STATE
    // ========================================
    setQuestionnaireData: (state, action: PayloadAction<Partial<QuestionnaireState>>) => {
      Object.assign(state, action.payload)
    },
    
    resetQuestionnaire: (state) => {
      // Keep sections but reset progress
      const sections = state.sections
      Object.assign(state, createInitialQuestionnaireState())
      state.sections = sections
    },
    
    fullResetQuestionnaire: () => createInitialQuestionnaireState(),
  },
})

export const {
  openQuestionnaire,
  closeQuestionnaire,
  toggleQuestionnaire,
  goToSection,
  goToQuestion,
  nextQuestion,
  previousQuestion,
  setAnswer,
  clearAnswer,
  skipQuestion,
  unskipQuestion,
  completeSection,
  uncompleteSection,
  completeQuestionnaire,
  reopenQuestionnaire,
  setSections,
  setProgress,
  setQuestionnaireData,
  resetQuestionnaire,
  fullResetQuestionnaire,
} = questionnaireSlice.actions

export default questionnaireSlice.reducer
